/**
 * /industries/real-estate data — rebuilt from
 * src/Design/industries/industry-real-estate.html, word for word. New file:
 * this page previously used its own bespoke component set (RealEstateHero,
 * FeatureGroups, WhoItsFor, PricingCalculator), now replaced by the same
 * shared IndustryPageTemplate every other industry page uses, per the
 * brief's "all seven pages must use the same components."
 *
 * Content-to-verify: "Confirm all pricing figures against the 360SafeOps
 * offer page." Checked against src/data/offers.js's managed-living-platform
 * (repriced per unit): $2,500 one-time onboarding fee, $5 per unit per
 * month, surveillance module add-on at $3 per camera, live the same day,
 * 7-unit to 1,000+-unit range, self-serve/instant provisioning, your own
 * branding/domain/service catalogue — every figure below matches that entry
 * exactly. "Results section describes what an operator gets, because there
 * is no client case study yet" — also confirmed (`client: null,
 * caseStudyPath: null` on that same offer entry).
 *
 * No product screenshot exists for 360SafeOps (checked again for this
 * rebuild) — the hero and challenge/solution/results photos are all real
 * building/property photography, never a fabricated app screenshot.
 *
 * Title tag and meta description are unchanged from the previous version of
 * this page.
 */
const SIGNUP_URL = "https://www.360safeops.com"

const realEstate = ({ heroImage, challengeImages = [], solutionImage, resultsImage }) => ({
  slug: "real-estate",
  industryName: "Real Estate",

  kicker: "Solutions for managed residential buildings",
  headline: "Every resident request tracked until it's done",
  subheading: "360SafeOps puts service requests, maintenance and bookings in one resident app, live the same day.",
  heroImage,
  heroImageAlt: "Real Estate work in progress",
  ctaHref: "/contact",
  ctaLabel: "Book your free consultation",
  badge: "$5 per unit per month · live the same day",
  stats: [
    { value: "Same day", label: "Your building goes live" },
    { value: "$2,500", label: "One-time onboarding fee" },
    { value: "7 to 1,000+", label: "Units per building", long: true },
    { value: "$3", label: "Per camera, surveillance module add-on" },
  ],

  challengesEyebrow: "What slows real estate teams down",
  challengesHeading: "Where resident service breaks down",
  challengesIntro: "Residents notice every slow response. Owners notice the ones nobody can explain.",
  challenges: [
    {
      image: challengeImages[0],
      heading: "Requests lost in calls and chats",
      detail: "Nobody can say which requests are still open, or who has them.",
    },
    {
      image: challengeImages[1],
      heading: "Maintenance assigned by hand",
      detail: "Housekeeping and repairs get passed around manually.",
    },
    {
      image: challengeImages[2],
      heading: "Visitors arrive unannounced",
      detail: "Gate staff call residents to check every visitor.",
    },
    {
      image: challengeImages[3],
      heading: "No view of service levels",
      detail: "Owners ask how long requests take, and there is no answer.",
    },
  ],

  solutionEyebrow: "What we set up for real estate",
  solutionHeading: "Everything a managed building needs, in one app",
  solutionOfferName: "360SafeOps",
  solutionFit: "A resident app and a management console, sold as a subscription, not a project.",
  solutionTimelineValue: "$5",
  solutionTimelineLabel: "per unit per month, plus a $2,500 one-time onboarding fee",
  solutionFeatures: [
    {
      iconPath: '<rect x="7" y="2" width="10" height="20" rx="2"/><path d="M11 18h2"/>',
      title: "For residents",
      detail: "Raise requests, book amenities and read notices.",
    },
    {
      iconPath: '<path d="M14.7 6.3a4 4 0 0 0 5 5L21 13l-8 8-3-3 8-8-1.3-1.3a4 4 0 0 1-5-5L13 2l1.7 4.3z"/>',
      title: "For staff",
      detail: "Housekeeping, maintenance and medical escalation, assigned straight away.",
    },
    {
      iconPath: '<path d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4z"/><path d="M10 21h4"/>',
      title: "Guest reception",
      detail: "Visitor pre-authorisation and gate notification.",
    },
    {
      iconPath: '<path d="M4 20V10M10 20V4M16 20v-8M22 20H2"/>',
      title: "For management",
      detail: "SLA tracking, staff workload, request ageing and reporting.",
    },
  ],
  solutionLinkLabel: "See 360SafeOps in full",
  solutionLinkHref: "/offers/managed-living-platform",
  solutionImage,
  solutionImageAlt: "360SafeOps in use",

  resultsEyebrow: "What operators get",
  resultsHeading: "What changes for your building",
  resultsMetrics: [
    { value: "Same day", label: "from signup to live, with no implementation project" },
    { value: "$2,500", label: "one-time onboarding, whether you run seven units or a thousand" },
  ],
  resultsSummary: "Every request is visible on one console, with who has it and how long it has been open.",
  resultsLinkLabel: "Sign up your building",
  resultsLinkHref: SIGNUP_URL,
  resultsImage,
  resultsImageAlt: "360SafeOps",

  // No real-estate-specific client exists — the Trusted By section is
  // hidden entirely for this page (TrustedBy.js returns null on an empty
  // list), not filled with clients from other industries.
  trustedByLogos: [],

  whyEyebrow: "Why Visiomate for real estate",
  whyHeading: "What you get with this team",
  whyIntro: "Three things that stay the same on every engagement.",
  whyLinkHref: "/contact",
  reasons: [
    { title: "No project to wait for", detail: "Self-serve signup with instant provisioning." },
    { title: "Your brand, not ours", detail: "Your own branding, domain and service catalogue." },
    { title: "Priced per unit", detail: "$5 per unit per month, plus a one-time $2,500 onboarding fee." },
  ],

  questionsEyebrow: "Questions",
  questionsHeading: "Questions buyers ask before they commit",
  questionsIntro: "Something else on your mind?",
  questionsLinkHref: "/contact",
  faqs: [
    {
      q: "How much does 360SafeOps cost?",
      a: "A $2,500 one-time onboarding fee, then $5 per unit per month. The surveillance module add-on is $3 per camera.",
    },
    { q: "How long does setup take?", a: "Your building goes live the same day you sign up." },
    { q: "Is there an onboarding fee?", a: "Yes — $2,500, one-time." },
    { q: "Does it work for small buildings?", a: "Yes, from seven units up to a thousand or more, on the same self-serve signup." },
  ],

  ctaHeading: "Start your building today",
  ctaDetail: "Sign up yourself and go live the same day.",
  ctaLinkLabel: "Sign up your building",
  ctaLinkHref: SIGNUP_URL,

  metaTitle: "Real Estate Software Development Company | Visiomate",
  metaDescription:
    "360SafeOps: a resident app for every managed service in a residential building, with scheduling, SLA tracking and billing — a per-unit subscription, live the same day.",
})

export default realEstate
